<!--
  One private note in the diff, under the last line it covers
  (docs/design.md "Notes"). It says it is seen by nobody else, who wrote it
  (the viewer, or one of their agents, by name), and when it came through a
  line map from another version of the file; a note none of whose lines are
  in the version shown (`lost`) says so. Edit changes its text in
  place; Delete hides it at once, with Undo in the notice.

  On a pull request (`promote`), Promote to draft makes it a draft
  comment on the lines it is at here (state/promotion.ts), or says why it
  can't be; a promoted note says so and leads to its draft (or, once
  published, to the conversation), wherever it shows. An agent's note is
  its result, rendered as the agent panel renders one (Html, which
  sanitizes it; `relay-body`).
-->
<script lang="ts">
  import { tick } from 'svelte';
  import { commentKeys } from '../keys/keyboard.svelte';
  import type { PlacedNote } from '../review/notes';
  import { linesText } from '../logic/promotion';
  import type { NotesStore } from '../state/notes.svelte';
  import { promotionText, type NotePromote } from '../state/promotion';
  import { toast } from '../state/toast.svelte';
  import Html from './Html.svelte';
  import PendingTag from './PendingTag.svelte';

  interface Props {
    placed: PlacedNote;
    store: NotesStore;
    /** None of its lines are in the version shown: it is listed with the file's comments. */
    lost?: boolean;
    /** The repository and the pull request on screen (none on a branch's page), which its promotion's link names. */
    here?: { owner: string; repo: string; number: number | null } | null;
    /** How it is promoted here: on a pull request only. */
    promote?: NotePromote | null;
  }
  let { placed, store, lost = false, here = null, promote = null }: Props = $props();

  const note = $derived(placed.note);
  const author = $derived(note.author.kind === 'agent' ? `${note.author.label} (your agent)` : 'You');
  const lines = $derived(placed.from === placed.to ? `line ${placed.from}` : `lines ${placed.from}–${placed.to}`);
  const saving = $derived(store.saving.has(note.id));
  const promoted = $derived(note.promotion && here ? promotionText(note.promotion, here) : null);
  let promoting = $state(false);

  async function promoteIt() {
    if (!promote || promote.plan.kind !== 'ready' || promoting) return;
    promoting = true;
    try {
      await promote.run();
    } finally {
      promoting = false;
    }
  }
  let editing = $state(false);
  let text = $state('');
  let failed: string | null = $state(null);
  let root: HTMLElement | undefined = $state();

  function edit() {
    text = note.body;
    failed = null;
    editing = true;
  }

  async function save() {
    if (!text.trim() || saving) return;
    const sent = text;
    editing = false;
    try {
      await store.edit(note.id, sent);
    } catch (e) {
      // The text comes back to the editor, with why.
      text = sent;
      failed = e instanceof Error ? e.message : String(e);
      editing = true;
    }
  }

  async function remove() {
    if (saving) return;
    const id = note.id;
    try {
      await store.remove(id);
    } catch (e) {
      toast.show({ text: `The note could not be deleted: ${e instanceof Error ? e.message : String(e)}` });
      return;
    }
    toast.show({
      text: 'Note deleted',
      action: {
        label: 'Undo',
        run: () =>
          void store.restore(id).catch((e: unknown) => toast.show({ text: `The note could not be brought back: ${e instanceof Error ? e.message : String(e)}` })),
      },
    });
  }

  async function cancel() {
    editing = false;
    failed = null;
    await tick();
    root?.querySelector<HTMLElement>('.note-edit')?.focus({ preventScroll: true });
  }

  // Mod+Enter saves, Esc cancels.
  const keys = commentKeys((c) => {
    if (c === 'submit') void save();
    else void cancel();
  });
</script>

<div bind:this={root} class="thread note" class:is-pending={saving} class:carried={placed.carried} aria-busy={saving ? 'true' : undefined} data-note={note.id}>
  <div class="comment" tabindex="-1">
    <div class="comment-head">
      <span class="badge note-private" title="A private note: nobody else sees it, and it is never posted to GitHub">Note · only you</span>
      <b>{author}</b>
      <span class="note-lines">{note.side === 'old' ? 'old ' : ''}{lines}{lost ? ` at ${note.commit_sha.slice(0, 7)}, not in this version` : ''}</span>
      {#if placed.carried && !lost}
        <span class="note-carried" title="Written on another version of this file ({note.commit_sha.slice(0, 7)}); its lines are followed to this one">from {note.commit_sha.slice(0, 7)}</span>
      {/if}
      {#if promoted}<a class="note-promoted" href={promoted.href}>{promoted.text}</a>{/if}
    </div>
    {#if editing}
      <div class="composer" style="border-color:var(--line)">
        <!-- svelte-ignore a11y_autofocus -->
        <textarea autofocus aria-label="Edit note" bind:value={text} {@attach keys}></textarea>
        <div class="row">
          {#if failed}<span class="note-failed" role="alert">Not saved: {failed}</span>{/if}
          <span class="spacer"></span>
          <button class="micro" onclick={cancel}>Cancel</button>
          <button class="micro primary" disabled={!text.trim()} onclick={save}>Save</button>
        </div>
      </div>
    {:else}
      <Html md={note.body} class={note.author.kind === 'agent' ? 'comment-body relay-body' : 'comment-body'} />
    {/if}
  </div>
  {#if !editing}
    <div class="thread-actions">
      <span class="spacer"></span>
      {#if saving}
        <PendingTag label="Saving" />
      {:else if note.id > 0}
        {#if promote && !note.promotion}
          {#if promote.plan.kind === 'refused'}
            <span class="note-why">Can’t be promoted here: {promote.plan.why}</span>
          {:else}
            {@const plan = promote.plan}
            <button
              class="micro note-promote"
              disabled={plan.kind !== 'ready' || !!promote.off || promoting}
              title={promote.off ??
                (plan.kind === 'ready'
                  ? `Make it a draft comment on ${linesText(plan.from, plan.to)}${plan.target.kind === 'file' ? ' (outside the diff, so a file comment naming them)' : ''}, for your review: nobody sees it until you publish`
                  : 'Finding its lines in this version…')}
              onclick={promoteIt}>Promote to draft</button
            >
          {/if}
        {/if}
        <button class="micro note-edit" onclick={edit}>Edit</button>
        <button class="micro" onclick={remove}>Delete</button>
      {/if}
    </div>
  {/if}
</div>

<style>
  /* Apart from drafts (the accent): a note is never posted. */
  .note {
    border-left: 3px solid var(--merged);
  }
  .note-private {
    color: var(--merged);
    border-color: var(--merged);
  }
  .note-lines,
  .note-carried {
    color: var(--muted);
    font-size: 0.85em;
  }
  .note-failed {
    color: var(--bad);
    font-size: 0.85em;
  }
  .note-promoted {
    font-size: 0.85em;
  }
  .note-why {
    color: var(--muted);
    font-size: 0.85em;
  }
  .note :global(.relay-body) {
    overflow-wrap: anywhere;
  }
</style>
